export type DirectoryShareAccessMode = 'PUBLIC' | 'PASSWORD';
export type DirectoryShareExpiryMode = 'ONE_DAY' | 'SEVEN_DAYS' | 'THIRTY_DAYS' | 'CUSTOM' | 'PERMANENT';
export type DirectoryShareStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'SUSPENDED_BY_VIP';
export type PublicShareState = 'AVAILABLE' | 'LOCKED' | 'UNAVAILABLE';

export interface DirectoryShare {
  id: number;
  directoryPath: string;
  sharePath: string;
  accessMode: DirectoryShareAccessMode;
  expiresAt: string | null;
  status: DirectoryShareStatus;
  passwordProtected: boolean;
}

export interface DirectoryShareMutation {
  directoryPath: string;
  accessMode: DirectoryShareAccessMode;
  password?: string;
  expiryMode: DirectoryShareExpiryMode;
  expiresAt?: string;
}

export interface PublicDirectoryShareInfo {
  state: PublicShareState;
  directoryName?: string;
  expiresAt?: string | null;
  total?: number;
}

export interface PublicDirectoryShareItem {
  fileName: string;
  contentType?: string;
  fileCategory: 'image' | 'video' | 'audio' | 'pdf' | 'other';
  sizeBytes?: number;
  size?: string;
  uploadTime: string;
  previewUrl: string;
  downloadUrl: string;
}

export interface PublicDirectoryShareItems {
  items: PublicDirectoryShareItem[];
  nextCursor?: string | null;
  hasMore: boolean;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
}

const readResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json() as ApiEnvelope<T>;
  if (!response.ok || !payload.success) {
    const error = new Error(payload.message || 'Request failed') as Error & {code?: string; status?: number};
    error.code = payload.code;
    error.status = response.status;
    throw error;
  }
  return payload.data as T;
};

export const getDirectoryShare = async (directoryPath: string): Promise<DirectoryShare | null> => {
  const response = await fetch(`/img/directory-shares?directoryPath=${encodeURIComponent(directoryPath)}`, {
    credentials: 'include',
  });
  return readResponse<DirectoryShare | null>(response);
};

export const createDirectoryShare = async (request: DirectoryShareMutation): Promise<DirectoryShare> => {
  const response = await fetch('/img/directory-shares', {
    method: 'POST',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(request),
  });
  return readResponse<DirectoryShare>(response);
};

export const updateDirectoryShare = async (
  id: number,
  request: DirectoryShareMutation,
): Promise<DirectoryShare> => {
  const response = await fetch(`/img/directory-shares/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(request),
  });
  return readResponse<DirectoryShare>(response);
};

export const revokeDirectoryShare = async (id: number): Promise<void> => {
  const response = await fetch(`/img/directory-shares/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await readResponse<boolean>(response);
};

export const getPublicDirectoryShare = async (shareCode: string): Promise<PublicDirectoryShareInfo> => {
  const response = await fetch(`/img/directory-shares/public/${encodeURIComponent(shareCode)}`, {
    credentials: 'include',
    cache: 'no-store',
  });
  return readResponse<PublicDirectoryShareInfo>(response);
};

export const unlockPublicDirectoryShare = async (
  shareCode: string,
  password: string,
): Promise<PublicDirectoryShareInfo> => {
  const response = await fetch(`/img/directory-shares/public/${encodeURIComponent(shareCode)}/unlock`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({password}),
  });
  return readResponse<PublicDirectoryShareInfo>(response);
};

export interface PublicItemsQuery {
  cursor?: string | null;
  pageSize?: number;
  keyword?: string;
  fileType?: string;
}

export const getPublicDirectoryShareItems = async (
  shareCode: string,
  query: PublicItemsQuery,
): Promise<PublicDirectoryShareItems> => {
  const params = new URLSearchParams();
  params.set('pageSize', String(query.pageSize || 50));
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.keyword) params.set('keyword', query.keyword);
  if (query.fileType && query.fileType !== 'all') params.set('fileType', query.fileType);
  const response = await fetch(
    `/img/directory-shares/public/${encodeURIComponent(shareCode)}/items?${params.toString()}`,
    {credentials: 'include', cache: 'no-store'},
  );
  return readResponse<PublicDirectoryShareItems>(response);
};
